import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Dialog, DialogBackdrop, DialogPopup } from '../../../../web/components-v2/Dialog';
import { ScoreDisplay } from '../../../voice-agent/components/ScoreDisplay';
import type { Scenario, ScenarioCompletion } from '../../../voice-agent/types/scenarios';
import { styleSheet } from './styles';

interface ScenarioDetailModalProps {
	scenario: Scenario;
	completions: ScenarioCompletion[];
	bestScore: number | null;
	attemptCount: number;
	onClose: () => void;
}

/**
 * ScenarioDetailModal displays detailed completion history for a specific scenario
 * with drill-down capability to view AI feedback notes
 */
export const ScenarioDetailModal: React.FC<ScenarioDetailModalProps> = ({
	scenario,
	completions,
	bestScore,
	attemptCount,
	onClose,
}) => {
	const [expandedCompletionId, setExpandedCompletionId] = useState<string | null>(null);
	const history = useHistory();

	const handlePractice = () => {
		// Navigate to queue page with the scenario pre-selected
		onClose();
		history.push(`/aida/queue?scenarioId=${encodeURIComponent(scenario.id)}`);
	};

	// Sort completions by date (newest first)
	const sortedCompletions = [...completions].sort(
		(a, b) =>
			new Date(b.completedDate || b.creationDate).getTime() - new Date(a.completedDate || a.creationDate).getTime()
	);

	const toggleExpand = (completionId: string) => {
		setExpandedCompletionId(expandedCompletionId === completionId ? null : completionId);
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	};

	const getStatusLabel = (status: ScenarioCompletion['status']) => {
		switch (status) {
			case 'Completed':
				return 'Completed';
			case 'Failed':
				return 'Failed';
			case 'Pending':
				return 'Pending';
			default:
				return 'Unknown';
		}
	};

	const getStatusStyle = (status: ScenarioCompletion['status']) => {
		switch (status) {
			case 'Failed':
				return styleSheet.historyStatusFailed;
			case 'Pending':
				return styleSheet.historyStatusPending;
			default:
				return styleSheet.historyStatus;
		}
	};

	return (
		<Dialog
			open={true}
			onOpenChange={open => {
				if (!open) {
					onClose();
				}
			}}
		>
			<DialogBackdrop styles={[styleSheet.modalBackdrop]} />
			<DialogPopup styles={[styleSheet.modalPopup]} showCloseButton={false} overlay={false}>
				<div className={css(styleSheet.modalHeader)}>
					<div className={css(styleSheet.modalHeaderInfo)}>
						<h2 className={css(styleSheet.modalTitle)}>{scenario.name}</h2>
						<p className={css(styleSheet.modalDescription)}>{scenario.description}</p>
					</div>
					<button className={css(styleSheet.modalCloseButton)} onClick={onClose} aria-label='Close'>
						<svg width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
							<path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
						</svg>
					</button>
				</div>

				<div className={css(styleSheet.modalBody)}>
					<div className={css(styleSheet.modalStatsRow)}>
						<div className={css(styleSheet.modalStat)}>
							<div className={css(styleSheet.modalStatLabel)}>Best Score</div>
							<div className={css(styleSheet.modalStatValue)}>
								{bestScore !== null ? <ScoreDisplay score={bestScore} size='small' /> : 'N/A'}
							</div>
						</div>
						<div className={css(styleSheet.modalStat)}>
							<div className={css(styleSheet.modalStatLabel)}>Attempts</div>
							<div className={css(styleSheet.modalStatValue)}>{attemptCount}</div>
						</div>
						<button className={css(styleSheet.modalPracticeButton)} onClick={handlePractice}>
							Practice
						</button>
					</div>

					<div className={css(styleSheet.historySection)}>
						<h3 className={css(styleSheet.historySectionTitle)}>History</h3>
						{sortedCompletions.length === 0 ? (
							<div className={css(styleSheet.noHistoryMessage)}>No completion history yet</div>
						) : (
							<div className={css(styleSheet.historyTable)}>
								{sortedCompletions.map(completion => {
									const isExpanded = expandedCompletionId === completion.id;
									return (
										<div key={completion.id} className={css(styleSheet.historyRow)}>
											<div
												className={css(styleSheet.historyRowMain)}
												onClick={() => completion.notes && toggleExpand(completion.id)}
											>
												<div className={css(styleSheet.historyDate)}>
													{formatDate(completion.completedDate || completion.creationDate)}
												</div>
												<div className={css(styleSheet.historyScore)}>
													{completion.score !== null ? (
														<ScoreDisplay score={completion.score} size='small' />
													) : (
														<span className={css(styleSheet.historyScoreNA)}>N/A</span>
													)}
												</div>
												<div className={css(styleSheet.historyStatus, getStatusStyle(completion.status))}>
													{getStatusLabel(completion.status)}
												</div>
												{completion.notes && (
													<svg
														className={css(
															styleSheet.historyExpandIcon,
															isExpanded && styleSheet.historyExpandIconRotated
														)}
														viewBox='0 0 24 24'
													>
														<path d='M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z' fill='currentColor' />
													</svg>
												)}
											</div>
											{isExpanded && completion.notes && (
												<div className={css(styleSheet.historyNotes)}>{completion.notes}</div>
											)}
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>
			</DialogPopup>
		</Dialog>
	);
};
